import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export interface ListedProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  description: string;
  sizes: string[];
  stock: number;
  brand: string;
  tags: string[];
  thumbnail: string;
  images: string[];
  video?: string; // 5-second product video URL
  videoThumbnail?: string; // Video thumbnail/poster image
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  rejectReason?: string;
  isTrending?: boolean;
  colors?: { name: string; hex: string; price: number; image: string; images: string[] }[];
  stockBreakdown?: Record<string, unknown>;
}

const STORAGE_KEY = 'listed_products';

function loadProductsFromLocal(): ListedProduct[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return parsed.map((p: ListedProduct) => ({ ...p, status: p.status ?? 'pending' }));
  } catch { return []; }
}

function saveProducts(products: ListedProduct[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
}

// All possible categories
const ALL_CATEGORIES = ['Track Pants', 'T-Shirts', 'Jeans', 'Hoodies', 'Shorts'];

const CATEGORY_ICONS: Record<string, string> = {
  'Track Pants': 'ri-run-line',
  'T-Shirts': 'ri-t-shirt-line',
  'Jeans': 'ri-scissors-cut-line',
  'Hoodies': 'ri-shirt-line',
  'Shorts': 'ri-men-line',
};

const CATEGORY_COLORS: Record<string, string> = {
  'Track Pants': 'bg-orange-500',
  'T-Shirts': 'bg-sky-500',
  'Jeans': 'bg-indigo-500',
  'Hoodies': 'bg-violet-500',
  'Shorts': 'bg-emerald-500',
};

// ── Stock Matrix helpers ──
type StockMatrix = Record<string, Record<string, string>>;
type SizeStockMap = Record<string, string>;

function matrixTotal(matrix: StockMatrix): number {
  return Object.values(matrix).reduce((sum, sizeMap) =>
    sum + Object.values(sizeMap).reduce((s, v) => s + (Number(v) || 0), 0), 0);
}

function sizeMapTotal(map: SizeStockMap, sizes: string[]): number {
  return sizes.reduce((sum, s) => sum + (Number(map[s]) || 0), 0);
}

function parseBreakdown(product: ListedProduct): {
  matrix: StockMatrix; sizeStock: SizeStockMap;
  mode: 'matrix' | 'colorOnly' | 'sizeOnly' | 'single';
} {
  const hasColors = (product.colors?.length ?? 0) > 0;
  const hasSizes = (product.sizes?.length ?? 0) > 0;
  const bd = product.stockBreakdown as Record<string, unknown> | undefined;

  if (hasColors && hasSizes) {
    const matrix: StockMatrix = {};
    product.colors!.forEach(cv => {
      matrix[cv.name] = {};
      product.sizes.forEach(s => {
        const val = (bd?.[cv.name] as Record<string, unknown>)?.[s];
        matrix[cv.name][s] = val != null ? String(val) : '';
      });
    });
    return { matrix, sizeStock: {}, mode: 'matrix' };
  }
  if (hasColors && !hasSizes) {
    const matrix: StockMatrix = {};
    product.colors!.forEach(cv => {
      const val = (bd as Record<string, unknown>)?.[cv.name];
      matrix[cv.name] = { '__nosize__': val != null ? String(val) : '' };
    });
    return { matrix, sizeStock: {}, mode: 'colorOnly' };
  }
  if (!hasColors && hasSizes) {
    const sizeStock: SizeStockMap = {};
    product.sizes.forEach(s => {
      const val = (bd as Record<string, unknown>)?.[s];
      sizeStock[s] = val != null ? String(val) : '';
    });
    return { matrix: {}, sizeStock, mode: 'sizeOnly' };
  }
  const val = (bd as Record<string, unknown>)?.['total'] ?? product.stock ?? 0;
  return { matrix: {}, sizeStock: { '__nosize__': String(val) }, mode: 'single' };
}

// ── Product Detail Modal ──
const STOCK_PRESETS = [5, 10, 25, 50, 100];

interface ProductDetailModalProps {
  product: ListedProduct;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onDelete: (id: string) => void;
  onUpdateStock: (id: string, newStock: number, breakdown: Record<string, unknown>) => void;
  onToggleTrending: (id: string) => void;
  onUpdateImages: (id: string, images: string[], thumbnail: string) => void;
  onUpdateVideo?: (id: string, videoUrl: string | null, videoThumbnail: string | null) => void;
}

function ProductDetailModal({ product, onClose, onApprove, onReject, onDelete, onUpdateStock, onToggleTrending, onUpdateImages, onUpdateVideo }: ProductDetailModalProps) {
  const [activeImg, setActiveImg] = useState(0);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stockSaved, setStockSaved] = useState(false);
  const [fillAllValue, setFillAllValue] = useState('');
  // Image management state
  const [showImageManager, setShowImageManager] = useState(false);
  const [editImages, setEditImages] = useState<string[]>([...product.images]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [imagesSaved, setImagesSaved] = useState(false);
  const [imgUploadLoading, setImgUploadLoading] = useState(false);
  // Video management state
  const [showVideoManager, setShowVideoManager] = useState(false);
  const [videoUrl, setVideoUrl] = useState(product.video || '');
  const [videoThumbnail, setVideoThumbnail] = useState(product.videoThumbnail || '');
  const [videoUploadLoading, setVideoUploadLoading] = useState(false);
  const [videoSaved, setVideoSaved] = useState(false);
  const MAX_VIDEO_SIZE_MB = 20; // 20MB max for 5-second video
  const MAX_VIDEO_DURATION_SEC = 5; // 5 seconds max

  const parsed = parseBreakdown(product);
  const [matrix, setMatrix] = useState<StockMatrix>(parsed.matrix);
  const [sizeStock, setSizeStock] = useState<SizeStockMap>(parsed.sizeStock);
  const mode = parsed.mode;

  const hasColors = (product.colors?.length ?? 0) > 0;

  const stockTotal = (() => {
    if (mode === 'matrix') return matrixTotal(matrix);
    if (mode === 'colorOnly') {
      return (product.colors ?? []).reduce((sum, cv) => sum + (Number(matrix[cv.name]?.['__nosize__']) || 0), 0);
    }
    if (mode === 'sizeOnly') return sizeMapTotal(sizeStock, product.sizes);
    return Number(sizeStock['__nosize__'] ?? 0);
  })();

  const stockStatus = stockTotal === 0 ? 'out' : stockTotal <= 5 ? 'low' : stockTotal <= 20 ? 'medium' : 'good';

  const REJECT_REASONS = [
    'Poor image quality', 'Incorrect category', 'Price too high / unrealistic',
    'Incomplete description', 'Duplicate product', 'Policy violation',
  ];

  const discount = product.originalPrice > product.price
    ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;

  const buildBreakdown = (): Record<string, unknown> => {
    if (mode === 'matrix') {
      const bd: Record<string, Record<string, number>> = {};
      (product.colors ?? []).forEach(cv => {
        bd[cv.name] = {};
        product.sizes.forEach(s => { bd[cv.name][s] = Number(matrix[cv.name]?.[s]) || 0; });
      });
      return bd;
    }
    if (mode === 'colorOnly') {
      const bd: Record<string, number> = {};
      (product.colors ?? []).forEach(cv => { bd[cv.name] = Number(matrix[cv.name]?.['__nosize__']) || 0; });
      return bd;
    }
    if (mode === 'sizeOnly') {
      const bd: Record<string, number> = {};
      product.sizes.forEach(s => { bd[s] = Number(sizeStock[s]) || 0; });
      return bd;
    }
    return { total: Number(sizeStock['__nosize__'] ?? 0) };
  };

  const handleSaveStock = () => {
    onUpdateStock(product.id, stockTotal, buildBreakdown());
    setStockSaved(true);
    setTimeout(() => setStockSaved(false), 2500);
  };

  const handleAddImageUrl = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    if (editImages.includes(url)) { setNewImageUrl(''); return; }
    setEditImages(prev => [...prev, url]);
    setNewImageUrl('');
  };

  const handleRemoveImage = (idx: number) => {
    setEditImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleMoveImage = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= editImages.length) return;
    setEditImages(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploadLoading(true);
    try {
      // Upload to Supabase storage
      const ext = file.name.split('.').pop() ?? 'jpg';
      const fileName = `product-${product.id}-${Date.now()}.${ext}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      // Get public URL
      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(uploadData.path);
      const publicUrl = urlData.publicUrl;
      setEditImages(prev => [...prev, publicUrl]);
    } catch {
      // Fallback: read as base64 data URL if Supabase storage not configured
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) setEditImages(prev => [...prev, dataUrl]);
      };
      reader.readAsDataURL(file);
    }
    setImgUploadLoading(false);
    e.target.value = '';
  };

  const handleSaveImages = async () => {
    if (editImages.length === 0) return;
    const thumbnail = editImages[0];
    onUpdateImages(product.id, editImages, thumbnail);
    setImagesSaved(true);
    setTimeout(() => { setImagesSaved(false); setShowImageManager(false); }, 2000);
  };

  // Video upload handler
  const handleUploadVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 20MB)
    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      alert(`Video file size must be less than ${MAX_VIDEO_SIZE_MB}MB. Please compress your video.`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please upload a valid video file (MP4, WebM, or QuickTime).');
      return;
    }

    setVideoUploadLoading(true);
    try {
      // Upload to Supabase storage
      const ext = file.name.split('.').pop() ?? 'mp4';
      const fileName = `product-video-${product.id}-${Date.now()}.${ext}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-videos')
        .upload(fileName, file, { upsert: true, contentType: file.type });
      
      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage.from('product-videos').getPublicUrl(uploadData.path);
      const publicUrl = urlData.publicUrl;
      
      setVideoUrl(publicUrl);
      
      // Generate thumbnail from first frame (using video poster)
      // For now, use the first product image as thumbnail
      setVideoThumbnail(product.thumbnail || editImages[0] || '');
    } catch (err) {
      console.error('Video upload error:', err);
      alert('Video upload failed. Please try again or use a URL.');
    }
    setVideoUploadLoading(false);
    e.target.value = '';
  };

  const handleSaveVideo = async () => {
    if (!videoUrl) return;
    onUpdateVideo?.(product.id, videoUrl, videoThumbnail || null);
    setVideoSaved(true);
    setTimeout(() => { setVideoSaved(false); }, 2000);
  };

  const fillAll = () => {
    if (!fillAllValue) return;
    if (mode === 'matrix') {
      const updated: StockMatrix = {};
      (product.colors ?? []).forEach(cv => {
        updated[cv.name] = {};
        product.sizes.forEach(s => { updated[cv.name][s] = fillAllValue; });
      });
      setMatrix(updated);
    } else if (mode === 'colorOnly') {
      const updated: StockMatrix = {};
      (product.colors ?? []).forEach(cv => { updated[cv.name] = { '__nosize__': fillAllValue }; });
      setMatrix(updated);
    } else if (mode === 'sizeOnly') {
      const updated: SizeStockMap = {};
      product.sizes.forEach(s => { updated[s] = fillAllValue; });
      setSizeStock(updated);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-gray-900 font-bold text-base">Product Details</h3>
            <p className="text-gray-400 text-xs">ID: {product.id}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Images - Bigger & Better Quality */}
          <div>
            <div className="w-full h-80 sm:h-96 rounded-2xl overflow-hidden bg-gray-100 mb-3">
              {product.images[activeImg] ? (
                <img 
                  src={product.images[activeImg]} 
                  alt={product.name} 
                  className="w-full h-full object-contain"
                  loading="eager"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-image-line text-4xl text-gray-300"></i>
                </div>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {product.images.map((img, idx) => (
                  <button key={idx} onClick={() => setActiveImg(idx)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer shrink-0 ${activeImg === idx ? 'border-orange-400 ring-2 ring-orange-200' : 'border-gray-200 hover:border-gray-300'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="text-gray-900 font-bold text-lg leading-tight">{product.name}</h4>
              <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-bold border ${
                product.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
                product.status === 'rejected' ? 'bg-red-50 text-red-600 border-red-200' :
                'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {product.status === 'approved' ? 'Approved' : product.status === 'rejected' ? 'Rejected' : 'Pending'}
              </span>
            </div>
            <p className="text-gray-500 text-sm">{product.category}{product.brand ? ` · ${product.brand}` : ''}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-gray-900 font-bold text-xl">₹{product.price}</span>
              {product.originalPrice > product.price && (
                <span className="text-gray-400 text-sm line-through">₹{product.originalPrice}</span>
              )}
              {discount > 0 && (
                <span className="bg-green-50 text-green-600 text-xs font-bold px-2 py-0.5 rounded-lg">{discount}% OFF</span>
              )}
            </div>
          </div>

          {/* Stock Management */}
          <div className={`rounded-2xl border-2 p-4 transition-all ${
            stockStatus === 'out' ? 'border-red-300 bg-red-50' :
            stockStatus === 'low' ? 'border-amber-300 bg-amber-50' :
            stockStatus === 'medium' ? 'border-yellow-200 bg-yellow-50' :
            'border-green-200 bg-green-50'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-stack-line text-gray-600 text-lg"></i>
                </div>
                <p className="text-gray-800 font-bold text-sm">Stock Management</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                stockStatus === 'out' ? 'bg-red-100 text-red-600' :
                stockStatus === 'low' ? 'bg-amber-100 text-amber-700' :
                stockStatus === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {stockStatus === 'out' ? 'Out of Stock' : stockStatus === 'low' ? 'Low Stock' :
                 stockStatus === 'medium' ? 'Medium Stock' : 'Good Stock'}
              </span>
            </div>

            {mode !== 'single' && (
              <div className="flex items-center gap-2 mb-4 p-2.5 bg-white rounded-xl border border-gray-200">
                <i className="ri-flashlight-line text-gray-400 text-sm shrink-0"></i>
                <span className="text-gray-600 text-xs font-semibold whitespace-nowrap">Sabko ek saath:</span>
                <input type="number" min="0" value={fillAllValue}
                  onChange={e => setFillAllValue(e.target.value)} placeholder="qty"
                  className="w-16 px-2 py-1 rounded-lg border border-gray-200 text-xs outline-none focus:border-orange-400 text-center" />
                <button onClick={fillAll}
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-colors">
                  Fill All
                </button>
              </div>
            )}

            {mode === 'matrix' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left text-gray-500 font-semibold pb-2 pr-2 whitespace-nowrap">Colour / Size</th>
                      {product.sizes.map(size => (
                        <th key={size} className="text-center text-gray-700 font-bold pb-2 px-1 min-w-[52px]">{size}</th>
                      ))}
                      <th className="text-center text-gray-500 font-semibold pb-2 px-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(product.colors ?? []).map(cv => {
                      const rowTotal = product.sizes.reduce((sum, s) => sum + (Number(matrix[cv.name]?.[s]) || 0), 0);
                      return (
                        <tr key={cv.name} className="border-t border-gray-100">
                          <td className="py-2 pr-2">
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                              <span className="w-4 h-4 rounded-full border border-gray-200 shrink-0" style={{ backgroundColor: cv.hex }}></span>
                              <span className="text-gray-700 font-semibold">{cv.name}</span>
                            </div>
                          </td>
                          {product.sizes.map(size => (
                            <td key={size} className="py-2 px-1">
                              <input type="number" min="0"
                                value={matrix[cv.name]?.[size] ?? ''}
                                onChange={e => setMatrix(prev => ({ ...prev, [cv.name]: { ...(prev[cv.name] ?? {}), [size]: e.target.value } }))}
                                placeholder="0"
                                className="w-full px-1 py-1.5 rounded-lg border border-gray-200 text-xs outline-none focus:border-orange-400 text-center bg-white" />
                            </td>
                          ))}
                          <td className="py-2 px-1 text-center">
                            <span className={`font-bold text-xs ${rowTotal > 0 ? 'text-green-600' : 'text-gray-400'}`}>{rowTotal}</span>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-gray-300">
                      <td className="py-2 pr-2 text-gray-500 font-semibold text-xs">Total</td>
                      {product.sizes.map(size => {
                        const colTotal = (product.colors ?? []).reduce((sum, cv) => sum + (Number(matrix[cv.name]?.[size]) || 0), 0);
                        return (
                          <td key={size} className="py-2 px-1 text-center">
                            <span className={`font-bold text-xs ${colTotal > 0 ? 'text-gray-700' : 'text-gray-300'}`}>{colTotal}</span>
                          </td>
                        );
                      })}
                      <td className="py-2 px-1 text-center">
                        <span className="font-bold text-sm text-gray-900">{stockTotal}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {mode === 'colorOnly' && (
              <div className="space-y-2">
                {(product.colors ?? []).map(cv => (
                  <div key={cv.name} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
                    <span className="w-7 h-7 rounded-full border-2 border-gray-200 shrink-0" style={{ backgroundColor: cv.hex }}></span>
                    <span className="text-gray-800 font-semibold text-sm flex-1">{cv.name}</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {STOCK_PRESETS.map(p => (
                        <button key={p} onClick={() => setMatrix(prev => ({ ...prev, [cv.name]: { '__nosize__': String(p) } }))}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer whitespace-nowrap ${
                            matrix[cv.name]?.['__nosize__'] === String(p) ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}>{p}</button>
                      ))}
                    </div>
                    <input type="number" min="0" value={matrix[cv.name]?.['__nosize__'] ?? ''}
                      onChange={e => setMatrix(prev => ({ ...prev, [cv.name]: { '__nosize__': e.target.value } }))}
                      placeholder="0"
                      className="w-14 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center bg-white" />
                  </div>
                ))}
              </div>
            )}

            {mode === 'sizeOnly' && (
              <div className="space-y-2">
                {product.sizes.map(size => (
                  <div key={size} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                      <span className="text-gray-700 font-bold text-sm">{size}</span>
                    </div>
                    <span className="text-gray-800 font-semibold text-sm flex-1">Size {size}</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {STOCK_PRESETS.map(p => (
                        <button key={p} onClick={() => setSizeStock(prev => ({ ...prev, [size]: String(p) }))}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer whitespace-nowrap ${
                            sizeStock[size] === String(p) ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}>{p}</button>
                      ))}
                    </div>
                    <input type="number" min="0" value={sizeStock[size] ?? ''}
                      onChange={e => setSizeStock(prev => ({ ...prev, [size]: e.target.value }))}
                      placeholder="0"
                      className="w-14 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center bg-white" />
                  </div>
                ))}
              </div>
            )}

            {mode === 'single' && (
              <div className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="flex gap-2 mb-3 flex-wrap">
                  {STOCK_PRESETS.map(p => (
                    <button key={p} onClick={() => setSizeStock({ '__nosize__': String(p) })}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                        sizeStock['__nosize__'] === String(p) ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                      }`}>{p} pcs</button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSizeStock(prev => ({ '__nosize__': String(Math.max(0, (Number(prev['__nosize__']) || 0) - 1)) }))}
                    className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 font-bold text-xl hover:bg-gray-200 cursor-pointer shrink-0">
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input type="number" min="0" value={sizeStock['__nosize__'] ?? ''}
                    onChange={e => setSizeStock({ '__nosize__': e.target.value })} placeholder="0"
                    className="flex-1 text-center text-2xl font-bold text-gray-900 py-2 rounded-xl border-2 border-gray-200 focus:border-gray-400 outline-none" />
                  <button onClick={() => setSizeStock(prev => ({ '__nosize__': String((Number(prev['__nosize__']) || 0) + 1) }))}
                    className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center font-bold text-xl hover:bg-gray-800 cursor-pointer shrink-0">
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>
            )}

            {stockTotal > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 h-2 bg-white rounded-full overflow-hidden border border-gray-200">
                  <div className={`h-full rounded-full transition-all duration-500 ${
                    stockStatus === 'low' ? 'bg-amber-400' : stockStatus === 'medium' ? 'bg-yellow-400' : 'bg-green-500'
                  }`} style={{ width: `${Math.min(100, (stockTotal / 200) * 100)}%` }}></div>
                </div>
                <span className="text-xs font-bold text-gray-600 shrink-0">Total: {stockTotal} units</span>
              </div>
            )}

            <button onClick={handleSaveStock}
              className="mt-3 w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-save-line"></i>Stock Save Karo ({stockTotal} units)
            </button>
            {stockSaved && (
              <div className="flex items-center justify-center gap-2 py-2 text-green-600 text-sm font-semibold">
                <i className="ri-checkbox-circle-fill text-lg"></i>Stock updated successfully!
              </div>
            )}
          </div>

          {/* ── Image Manager ── */}
          <div className="rounded-2xl border-2 border-gray-100 overflow-hidden">
            <button
              onClick={() => setShowImageManager(p => !p)}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-gray-900 flex items-center justify-center shrink-0">
                  <i className="ri-image-edit-line text-white text-sm"></i>
                </div>
                <div className="text-left">
                  <p className="text-gray-900 font-bold text-sm">Product Images</p>
                  <p className="text-gray-400 text-xs">{editImages.length} image{editImages.length !== 1 ? 's' : ''} · Click to manage</p>
                </div>
              </div>
              {showImageManager ? <i className="ri-arrow-up-s-line text-gray-400 text-lg"></i> : <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>}
            </button>

            {showImageManager && (
              <div className="px-4 pb-4 bg-white border-t border-gray-100">
                {/* Current Images Grid */}
                {editImages.length > 0 && (
                  <div className="mt-3 mb-3">
                    <p className="text-gray-500 text-xs font-semibold mb-2">Current Images (drag to reorder · first = thumbnail)</p>
                    <div className="grid grid-cols-3 gap-2">
                      {editImages.map((img, idx) => (
                        <div key={idx} className="relative group rounded-xl overflow-hidden border-2 border-gray-100" style={{ height: '90px' }}>
                          <img src={img} alt={`img-${idx}`} className="w-full h-full object-cover" loading="lazy" />
                          {/* Overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center gap-1">
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleMoveImage(idx, idx - 1)} disabled={idx === 0}
                                className="w-6 h-6 rounded-full bg-white/80 text-gray-700 flex items-center justify-center disabled:opacity-30 cursor-pointer">
                                <i className="ri-arrow-left-s-line text-xs"></i>
                              </button>
                              <button onClick={() => handleRemoveImage(idx)}
                                className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer">
                                <i className="ri-delete-bin-line text-xs"></i>
                              </button>
                              <button onClick={() => handleMoveImage(idx, idx + 1)} disabled={idx === editImages.length - 1}
                                className="w-6 h-6 rounded-full bg-white/80 text-gray-700 flex items-center justify-center disabled:opacity-30 cursor-pointer">
                                <i className="ri-arrow-right-s-line text-xs"></i>
                              </button>
                            </div>
                          </div>
                          {/* Thumbnail label */}
                          {idx === 0 && (
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[8px] font-bold">THUMB</div>
                          )}
                          {/* Index */}
                          <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-black/60 text-white text-[9px] flex items-center justify-center font-bold">{idx + 1}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload new image */}
                <div className="mb-3">
                  <p className="text-gray-500 text-xs font-semibold mb-2">Upload New Image</p>
                  <label className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed transition-all cursor-pointer ${imgUploadLoading ? 'border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-300 hover:border-gray-500 text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                    {imgUploadLoading ? (
                      <><i className="ri-loader-4-line animate-spin text-sm"></i><span className="text-xs font-semibold">Uploading...</span></>
                    ) : (
                      <><i className="ri-upload-cloud-2-line text-lg"></i><span className="text-xs font-semibold">Tap to upload from device</span></>
                    )}
                    <input type="file" accept="image/*" onChange={handleUploadImage} className="hidden" disabled={imgUploadLoading} />
                  </label>
                </div>

                {/* Add by URL */}
                <div className="mb-4">
                  <p className="text-gray-500 text-xs font-semibold mb-2">Add by URL</p>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newImageUrl}
                      onChange={e => setNewImageUrl(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddImageUrl()}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-xs outline-none focus:border-gray-400"
                    />
                    <button
                      onClick={handleAddImageUrl}
                      disabled={!newImageUrl.trim()}
                      className="px-3 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold cursor-pointer disabled:opacity-40 whitespace-nowrap"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Save button */}
                <button
                  onClick={handleSaveImages}
                  disabled={editImages.length === 0}
                  className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer disabled:opacity-40 whitespace-nowrap"
                >
                  <i className="ri-save-line"></i>
                  {imagesSaved ? 'Images Saved! ✓' : `Save Images (${editImages.length})`}
                </button>
                {imagesSaved && (
                  <div className="flex items-center justify-center gap-2 py-2 text-green-600 text-sm font-semibold">
                    <i className="ri-checkbox-circle-fill text-lg"></i>Images updated successfully!
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Video Manager (5-Second Product Video) ── */}
          <div className="rounded-2xl border-2 border-purple-100 overflow-hidden">
            <button
              onClick={() => setShowVideoManager(p => !p)}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
                  <i className="ri-video-line text-white text-sm"></i>
                </div>
                <div className="text-left">
                  <p className="text-gray-900 font-bold text-sm">Product Video</p>
                  <p className="text-gray-500 text-xs">
                    {videoUrl ? '5-sec video uploaded ✓' : 'Add 5-second showcase video'}
                  </p>
                </div>
              </div>
              {showVideoManager ? <i className="ri-arrow-up-s-line text-gray-400 text-lg"></i> : <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>}
            </button>

            {showVideoManager && (
              <div className="px-4 pb-4 bg-white border-t border-purple-100">
                {/* Video Preview */}
                {videoUrl && (
                  <div className="mt-3 mb-3">
                    <p className="text-gray-500 text-xs font-semibold mb-2">Current Video Preview</p>
                    <div className="relative rounded-xl overflow-hidden border-2 border-purple-100 bg-black" style={{ height: '160px' }}>
                      <video
                        src={videoUrl}
                        poster={videoThumbnail || undefined}
                        className="w-full h-full object-contain"
                        controls
                        preload="metadata"
                      />
                      <button
                        onClick={() => { setVideoUrl(''); setVideoThumbnail(''); }}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload Video */}
                {!videoUrl && (
                  <div className="mb-3">
                    <p className="text-gray-500 text-xs font-semibold mb-2">Upload 5-Second Video</p>
                    <label className={`w-full flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed transition-all cursor-pointer ${videoUploadLoading ? 'border-purple-200 bg-purple-50 text-purple-400' : 'border-purple-300 hover:border-purple-500 text-purple-600 hover:bg-purple-50'}`}>
                      {videoUploadLoading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-lg"></i>
                          <span className="text-xs font-semibold">Uploading video...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-video-upload-line text-2xl"></i>
                          <span className="text-xs font-semibold">Tap to upload 5-sec video</span>
                          <span className="text-[10px] text-purple-400">Max 20MB · MP4/WebM</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        accept="video/mp4,video/webm,video/quicktime" 
                        onChange={handleUploadVideo} 
                        className="hidden" 
                        disabled={videoUploadLoading} 
                      />
                    </label>
                  </div>
                )}

                {/* Video URL Input */}
                {!videoUrl && (
                  <div className="mb-4">
                    <p className="text-gray-500 text-xs font-semibold mb-2">Or Add Video URL</p>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={videoUrl}
                        onChange={e => setVideoUrl(e.target.value)}
                        placeholder="https://..."
                        className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-xs outline-none focus:border-purple-400"
                      />
                      <button
                        onClick={() => videoUrl && onUpdateVideo?.(product.id, videoUrl, videoThumbnail)}
                        disabled={!videoUrl.trim()}
                        className="px-3 py-2 rounded-xl bg-purple-600 text-white text-xs font-bold cursor-pointer disabled:opacity-40 whitespace-nowrap"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Save Video Button */}
                {videoUrl && (
                  <button
                    onClick={handleSaveVideo}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:from-purple-600 hover:to-pink-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line"></i>
                    {videoSaved ? 'Video Saved! ✓' : 'Save Video'}
                  </button>
                )}
                {videoSaved && (
                  <div className="flex items-center justify-center gap-2 py-2 text-green-600 text-sm font-semibold">
                    <i className="ri-checkbox-circle-fill text-lg"></i>Video updated successfully!
                  </div>
                )}

                {/* Info Note */}
                <div className="mt-3 p-3 rounded-xl bg-purple-50 border border-purple-100">
                  <p className="text-purple-700 text-xs flex items-start gap-2">
                    <i className="ri-information-line text-purple-500 mt-0.5"></i>
                    <span>
                      <strong>5-Second Video:</strong> Short product showcase video customers ko better idea deta hai. Video product detail page pe auto-play hoga (muted).
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Description</p>
            <p className="text-gray-700 text-sm leading-relaxed">{product.description || '—'}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-gray-400 text-xs mb-1">Sizes</p>
              <p className="text-gray-800 text-sm font-semibold">{product.sizes.length > 0 ? product.sizes.join(', ') : 'Not specified'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-gray-400 text-xs mb-1">Images</p>
              <p className="text-gray-800 text-sm font-semibold">{product.images.length} uploaded</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-gray-400 text-xs mb-1">Listed On</p>
              <p className="text-gray-800 text-sm font-semibold">
                {new Date(product.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-gray-400 text-xs mb-1">Category</p>
              <p className="text-gray-800 text-sm font-semibold">{product.category}</p>
            </div>
          </div>

          {product.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {product.tags.map(tag => (
                <span key={tag} className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">#{tag}</span>
              ))}
            </div>
          )}

          {product.status === 'rejected' && product.rejectReason && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3">
              <p className="text-red-600 text-xs font-semibold mb-1 flex items-center gap-1"><i className="ri-error-warning-line"></i> Rejection Reason</p>
              <p className="text-red-700 text-sm">{product.rejectReason}</p>
            </div>
          )}

          {showRejectInput && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 space-y-2">
              <p className="text-red-700 text-xs font-semibold">Select rejection reason:</p>
              <div className="space-y-1.5">
                {REJECT_REASONS.map(r => (
                  <button key={r} onClick={() => setRejectReason(r)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer ${
                      rejectReason === r ? 'bg-red-100 border-red-400 text-red-700' : 'bg-white border-gray-200 text-gray-700 hover:border-red-300'
                    }`}>
                    <i className={`mr-2 ${rejectReason === r ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                    {r}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                  className="flex-1 py-2 rounded-xl bg-gray-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={() => { if (rejectReason) { onReject(product.id, rejectReason); onClose(); } }}
                  disabled={!rejectReason}
                  className="flex-1 py-2 rounded-xl bg-red-500 text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">Confirm Reject</button>
              </div>
            </div>
          )}

          {showDeleteConfirm && (
            <div className="bg-gray-900 rounded-xl p-3 space-y-2">
              <p className="text-white text-sm font-semibold">Delete this product permanently?</p>
              <p className="text-gray-400 text-xs">This cannot be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-xl bg-gray-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={() => { onDelete(product.id); onClose(); }}
                  className="flex-1 py-2 rounded-xl bg-red-500 text-white text-xs font-bold cursor-pointer whitespace-nowrap">Delete</button>
              </div>
            </div>
          )}
        </div>

        {!showRejectInput && !showDeleteConfirm && (
          <div className="px-5 py-4 border-t border-gray-100 shrink-0 space-y-2">
            <button onClick={() => onToggleTrending(product.id)}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                product.isTrending ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
              }`}>
              <i className="ri-fire-fill"></i>
              {product.isTrending ? 'Trending Banner Pe Hai ✓' : 'Trending Banner Pe Lagao'}
            </button>
            {product.status !== 'approved' && (
              <button onClick={() => { onApprove(product.id); onClose(); }}
                className="w-full py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-line"></i>Approve Product
              </button>
            )}
            {product.status !== 'rejected' && (
              <button onClick={() => setShowRejectInput(true)}
                className="w-full py-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-bold text-sm border border-red-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-close-circle-line"></i>Reject Product
              </button>
            )}
            {product.status === 'approved' && (
              <button onClick={() => { onReject(product.id, 'Removed by admin'); onClose(); }}
                className="w-full py-3 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold text-sm border border-amber-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-eye-off-line"></i>Unpublish
              </button>
            )}
            <button onClick={() => setShowDeleteConfirm(true)}
              className="w-full py-2.5 rounded-xl text-gray-400 hover:text-red-500 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1">
              <i className="ri-delete-bin-line"></i>Delete Permanently
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
// ── Product Card (compact) ──
interface ProductCardProps {
  product: ListedProduct;
  onSelect: (p: ListedProduct) => void;
  onApprove: (id: string) => void;
  onDelete: (id: string) => void;
}

function ProductCard({ product, onSelect, onApprove, onDelete }: ProductCardProps) {
  const discount = product.originalPrice > product.price
    ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;
  const stockLow = (product.stock ?? 0) <= 5;
  const stockOut = (product.stock ?? 0) === 0;

  return (
    <div onClick={() => onSelect(product)}
      className="bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all cursor-pointer overflow-hidden">
      <div className="flex items-center gap-3 p-3">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 shrink-0 border border-gray-100 relative">
          {product.thumbnail ? (
            <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <i className="ri-image-line text-gray-300 text-xl"></i>
            </div>
          )}
          {stockOut && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
              <span className="text-white text-[9px] font-bold">OUT</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-gray-900 font-semibold text-sm truncate">{product.name}</p>
            <div className="flex items-center gap-1 shrink-0">
              {product.isTrending && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 flex items-center gap-0.5">
                  <i className="ri-fire-fill text-[9px]"></i>
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                product.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
                product.status === 'rejected' ? 'bg-red-50 text-red-600 border-red-200' :
                'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {product.status === 'approved' ? 'Live' : product.status === 'rejected' ? 'Rejected' : 'Review'}
              </span>
            </div>
          </div>
          <p className="text-gray-400 text-xs mt-0.5">{product.brand || '—'}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-gray-900 font-bold text-sm">₹{product.price}</span>
            {product.originalPrice > product.price && (
              <span className="text-gray-400 text-xs line-through">₹{product.originalPrice}</span>
            )}
            {discount > 0 && <span className="text-green-600 text-xs font-semibold">{discount}% off</span>}
            <span className={`ml-auto text-xs font-semibold ${stockOut ? 'text-red-500' : stockLow ? 'text-amber-500' : 'text-gray-400'}`}>
              {stockOut ? 'Out' : `${product.stock ?? 0} pcs`}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
          {product.status !== 'approved' && (
            <button onClick={() => onApprove(product.id)}
              className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center text-green-600 hover:bg-green-100 transition-colors cursor-pointer" title="Approve">
              <i className="ri-check-line text-sm"></i>
            </button>
          )}
          <button onClick={() => onDelete(product.id)}
            className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer" title="Delete">
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ProductsPanel ──
export default function ProductsPanel() {
  const [products, setProducts] = useState<ListedProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ListedProduct | null>(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [toastMsg, setToastMsg] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  // View mode: 'pending' = pending review, 'approved' = category-wise approved
  const [viewMode, setViewMode] = useState<'pending' | 'approved'>('pending');

  const showToast = useCallback((text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 3000);
  }, []);

  // Load from Supabase (primary) + localStorage (fallback)
  const loadProductsFromSupabase = useCallback(async (): Promise<ListedProduct[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Supabase fetch error:', error);
        return loadProductsFromLocal();
      }
      
      if (!data || data.length === 0) {
        return loadProductsFromLocal();
      }
      
      // Map Supabase data to ListedProduct format
      return data.map((p: Record<string, unknown>) => ({
        id: p.id as string,
        name: p.name as string,
        category: p.category as string,
        price: p.price as number,
        originalPrice: (p.original_price as number) || (p.price as number),
        description: p.description as string,
        sizes: (p.sizes as string[]) || [],
        stock: p.stock as number,
        brand: p.brand as string,
        tags: (p.tags as string[]) || [],
        thumbnail: p.thumbnail as string,
        images: (p.images as string[]) || [],
        video: (p.video as string) || undefined,
        videoThumbnail: (p.video_thumbnail as string) || undefined,
        status: (p.status as 'pending' | 'approved' | 'rejected') || 'pending',
        createdAt: p.created_at as string,
        rejectReason: p.reject_reason as string,
        isTrending: p.is_trending as boolean,
        colors: p.colors as { name: string; hex: string; price: number; image: string; images: string[] }[] | undefined,
        stockBreakdown: p.stock_breakdown as Record<string, unknown> | undefined,
      }));
    } catch (e) {
      console.error('Error loading from Supabase:', e);
      return loadProductsFromLocal();
    }
  }, []);

  useEffect(() => { 
    loadProductsFromSupabase().then(products => {
      setProducts(products);
      // Also save to localStorage so Portfolio can access these products
      if (products.length > 0) {
        saveProducts(products);
      }
    });
  }, [loadProductsFromSupabase]);

  // Listen for AI bot product added event
  useEffect(() => {
    const handleProductAdded = () => {
      // Refresh products from Supabase
      loadProductsFromSupabase().then(freshProducts => {
        setProducts(freshProducts);
        if (freshProducts.length > 0) {
          saveProducts(freshProducts);
        }
      });
    };

    window.addEventListener('ai-product-added', handleProductAdded);
    return () => window.removeEventListener('ai-product-added', handleProductAdded);
  }, [loadProductsFromSupabase]);

  const handleApprove = useCallback(async (id: string) => {
    // Update in Supabase
    const { error } = await supabase
      .from('products')
      .update({ status: 'approved', reject_reason: null, updated_at: new Date().toISOString() })
      .eq('id', id);
    
    if (error) {
      console.error('Supabase update error:', error);
      showToast('Failed to approve product. Try again.', 'error');
      return;
    }
    
    // Immediately update local state for instant UI feedback
    setProducts(prev => {
      const updated = prev.map(p => {
        if (p.id === id) {
          return { ...p, status: 'approved' as const, rejectReason: undefined };
        }
        return p;
      });
      saveProducts(updated);
      return updated;
    });
    
    // Close modal if open
    setSelectedProduct(prev => prev && prev.id === id ? { ...prev, status: 'approved', rejectReason: undefined } : prev);
    
    // Force refresh from Supabase to ensure data is synced (with delay for DB replication)
    setTimeout(async () => {
      const { data: refreshedData, error: refreshError } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      
      if (refreshError) {
        console.error('Refresh error:', refreshError);
      } else if (refreshedData) {
        // Update with confirmed data from Supabase
        setProducts(prev => {
          const updated = prev.map(p => {
            if (p.id === id) {
              return {
                ...p,
                status: (refreshedData.status as 'pending' | 'approved' | 'rejected') || 'approved',
                rejectReason: refreshedData.reject_reason as string | undefined,
                stock: refreshedData.stock as number,
                stockBreakdown: refreshedData.stock_breakdown as Record<string, unknown> | undefined,
              };
            }
            return p;
          });
          saveProducts(updated);
          return updated;
        });
      }
      
      // Also refresh full list to ensure consistency
      loadProductsFromSupabase().then(freshProducts => {
        setProducts(freshProducts);
      });
    }, 300);
    
    showToast('Product approved! Now visible in store.', 'success');
  }, [showToast, loadProductsFromSupabase]);

  const handleReject = useCallback(async (id: string, reason: string) => {
    // Update in Supabase
    const { error } = await supabase
      .from('products')
      .update({ status: 'rejected', reject_reason: reason, updated_at: new Date().toISOString() })
      .eq('id', id);
    
    if (error) {
      console.error('Supabase update error:', error);
      showToast('Failed to reject product. Try again.', 'error');
      return;
    }
    
    // Immediately update local state for instant UI feedback
    setProducts(prev => {
      const updated = prev.map(p => {
        if (p.id === id) {
          return { ...p, status: 'rejected' as const, rejectReason: reason };
        }
        return p;
      });
      saveProducts(updated);
      return updated;
    });
    
    // Close modal if open
    setSelectedProduct(prev => prev && prev.id === id ? { ...prev, status: 'rejected', rejectReason: reason } : prev);
    
    // Force refresh from Supabase to ensure data is synced (with delay for DB replication)
    setTimeout(async () => {
      const { data: refreshedData, error: refreshError } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      
      if (refreshError) {
        console.error('Refresh error:', refreshError);
      } else if (refreshedData) {
        // Update with confirmed data from Supabase
        setProducts(prev => {
          const updated = prev.map(p => {
            if (p.id === id) {
              return {
                ...p,
                status: (refreshedData.status as 'pending' | 'approved' | 'rejected') || 'rejected',
                rejectReason: refreshedData.reject_reason as string | undefined,
                stock: refreshedData.stock as number,
                stockBreakdown: refreshedData.stock_breakdown as Record<string, unknown> | undefined,
              };
            }
            return p;
          });
          saveProducts(updated);
          return updated;
        });
      }
      
      // Also refresh full list to ensure consistency
      loadProductsFromSupabase().then(freshProducts => {
        setProducts(freshProducts);
      });
    }, 300);
    
    showToast('Product rejected.', 'error');
  }, [showToast, loadProductsFromSupabase]);

  const handleDelete = useCallback(async (id: string) => {
    // First delete from Supabase (if it's a Supabase product with UUID)
    if (!id.startsWith('PROD-')) {
      // It's a Supabase product (UUID format), delete from Supabase
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) {
        console.error('Supabase delete error:', error);
        showToast('Failed to delete from server. Please try again.', 'error');
        return;
      }
    }
    
    // Immediately update local state for instant UI feedback
    setProducts(prev => {
      const updated = prev.filter(p => p.id !== id);
      saveProducts(updated);
      return updated;
    });
    
    // Close modal if open
    setSelectedProduct(prev => prev && prev.id === id ? null : prev);
    
    // Force refresh from Supabase to ensure data is synced (with delay for DB replication)
    setTimeout(async () => {
      loadProductsFromSupabase().then(freshProducts => {
        setProducts(freshProducts);
      });
    }, 300);
    
    showToast('Product deleted permanently.', 'info');
  }, [showToast, loadProductsFromSupabase]);

  const handleUpdateStock = useCallback(async (id: string, newStock: number, breakdown: Record<string, unknown>) => {
    // Get current stock before update for history log
    const currentProduct = products.find(p => p.id === id);
    const stockBefore = currentProduct?.stock ?? 0;

    const { error } = await supabase
      .from('products')
      .update({ stock: newStock, stock_breakdown: breakdown })
      .eq('id', id);
    
    if (error) {
      console.error('Supabase stock update error:', error);
      showToast('Failed to update stock. Try again.', 'error');
      return;
    }

    // Log to stock history
    try {
      const { logStockChange } = await import('@/lib/stockHistory');
      await logStockChange({
        product_id: id,
        product_name: currentProduct?.name ?? 'Unknown',
        category: currentProduct?.category ?? '',
        change_type: 'manual_update',
        change_amount: Math.abs(newStock - stockBefore),
        stock_before: stockBefore,
        stock_after: newStock,
        note: 'Products panel se manual stock update',
        performed_by: 'admin',
      });
    } catch { /* ignore log errors */ }
    
    setProducts(prev => {
      const updated = prev.map(p =>
        p.id === id ? { ...p, stock: newStock, outOfStock: newStock === 0, stockBreakdown: breakdown } : p
      );
      saveProducts(updated);
      setSelectedProduct(sel => sel && sel.id === id ? { ...sel, stock: newStock, stockBreakdown: breakdown } : sel);
      return updated;
    });
    showToast(`Stock updated to ${newStock} units!`, 'success');
  }, [showToast, products]);

  const handleUpdateImages = useCallback(async (id: string, images: string[], thumbnail: string) => {
    const { error } = await supabase
      .from('products')
      .update({ images, thumbnail, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Supabase image update error:', error);
      showToast('Failed to update images. Try again.', 'error');
      return;
    }

    setProducts(prev => {
      const updated = prev.map(p =>
        p.id === id ? { ...p, images, thumbnail } : p
      );
      saveProducts(updated);
      setSelectedProduct(sel => sel && sel.id === id ? { ...sel, images, thumbnail } : sel);
      return updated;
    });
    showToast('Images updated successfully!', 'success');
  }, [showToast]);

  const handleUpdateVideo = useCallback(async (id: string, videoUrl: string | null, videoThumbnail: string | null) => {
    const { error } = await supabase
      .from('products')
      .update({ 
        video: videoUrl, 
        video_thumbnail: videoThumbnail,
        updated_at: new Date().toISOString() 
      })
      .eq('id', id);

    if (error) {
      console.error('Supabase video update error:', error);
      showToast('Failed to update video. Try again.', 'error');
      return;
    }

    setProducts(prev => {
      const updated = prev.map(p =>
        p.id === id ? { ...p, video: videoUrl || undefined, videoThumbnail: videoThumbnail || undefined } : p
      );
      saveProducts(updated);
      setSelectedProduct(sel => sel && sel.id === id ? { ...sel, video: videoUrl || undefined, videoThumbnail: videoThumbnail || undefined } : sel);
      return updated;
    });
    showToast('Video updated successfully!', 'success');
  }, [showToast]);

  const handleToggleTrending = useCallback(async (id: string) => {
    const product = products.find(p => p.id === id);
    const newTrendingStatus = !product?.isTrending;
    
    // Update in Supabase
    const { error } = await supabase
      .from('products')
      .update({ is_trending: newTrendingStatus })
      .eq('id', id);
    
    if (error) {
      console.error('Supabase trending update error:', error);
      showToast('Failed to update trending status. Try again.', 'error');
      return;
    }
    
    setProducts(prev => {
      const updated = prev.map(p => p.id === id ? { ...p, isTrending: newTrendingStatus } : p);
      saveProducts(updated);
      const trendingList = updated.filter(p => p.isTrending).map(p => ({
        id: p.id, name: p.name, category: p.category, price: p.price,
        originalPrice: p.originalPrice, sizes: p.sizes, thumbnail: '', images: [],
      }));
      try { localStorage.setItem('trending_products', JSON.stringify(trendingList)); } catch { /* quota */ }
      setSelectedProduct(sel => sel && sel.id === id ? { ...sel, isTrending: newTrendingStatus } : sel);
      showToast(newTrendingStatus ? 'Trending banner pe lagaya!' : 'Trending banner se hataya!', 'success');
      return updated;
    });
  }, [showToast, products]);

  const counts = {
    total: products.length,
    pending: products.filter(p => p.status === 'pending').length,
    approved: products.filter(p => p.status === 'approved').length,
    rejected: products.filter(p => p.status === 'rejected').length,
  };

  // Filter by search
  const searchFilter = (p: ListedProduct) => {
    const q = search.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || (p.brand ?? '').toLowerCase().includes(q);
  };

  // Pending products
  const pendingProducts = products.filter(p => p.status === 'pending' && searchFilter(p));

  // Approved products by category
  const approvedProducts = products.filter(p => p.status === 'approved' && searchFilter(p));
  const rejectedProducts = products.filter(p => p.status === 'rejected' && searchFilter(p));

  // Get unique categories from approved products + all predefined
  const approvedCategories = ALL_CATEGORIES.filter(cat =>
    approvedProducts.some(p => p.category === cat)
  );
  const otherCategories = [...new Set(approvedProducts.map(p => p.category))].filter(c => !ALL_CATEGORIES.includes(c));
  const displayCategories = [...approvedCategories, ...otherCategories];

  const filteredApproved = activeCategory === 'all'
    ? approvedProducts
    : approvedProducts.filter(p => p.category === activeCategory);

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toastMsg && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${
          toastMsg.type === 'success' ? 'bg-green-500' : toastMsg.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
        }`}>
          <i className={toastMsg.type === 'success' ? 'ri-check-line' : toastMsg.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
          {toastMsg.text}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Total',    value: counts.total,    color: 'bg-gray-900',  icon: 'ri-apps-line' },
          { label: 'Pending',  value: counts.pending,  color: 'bg-amber-500', icon: 'ri-time-line' },
          { label: 'Approved', value: counts.approved, color: 'bg-green-500', icon: 'ri-checkbox-circle-line' },
          { label: 'Rejected', value: counts.rejected, color: 'bg-red-400',   icon: 'ri-close-circle-line' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl p-3 border border-gray-100 text-center">
            <div className={`w-8 h-8 rounded-xl ${s.color} flex items-center justify-center mx-auto mb-1.5`}>
              <i className={`${s.icon} text-white text-sm`}></i>
            </div>
            <p className="text-xl font-bold text-gray-900">{s.value}</p>
            <p className="text-gray-400 text-xs">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, category, brand..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none" />
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 bg-gray-100 rounded-2xl p-1">
        <button onClick={() => setViewMode('pending')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            viewMode === 'pending' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:text-gray-800'
          }`}>
          <i className="ri-time-line"></i>
          Pending Review
          {counts.pending > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              viewMode === 'pending' ? 'bg-white/25 text-white' : 'bg-amber-100 text-amber-700'
            }`}>
              {counts.pending}
            </span>
          )}
        </button>
        <button onClick={() => setViewMode('approved')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            viewMode === 'approved' ? 'bg-green-500 text-white' : 'text-gray-600 hover:text-gray-800'
          }`}>
          <i className="ri-apps-line"></i>
          Category Wise
          {counts.approved > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              viewMode === 'approved' ? 'bg-white/25 text-white' : 'bg-green-100 text-green-700'
            }`}>
              {counts.approved}
            </span>
          )}
        </button>
      </div>

      {/* ── PENDING REVIEW SECTION ── */}
      {viewMode === 'pending' && (
        <>
          {pendingProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-3xl text-green-400"></i>
              </div>
              <div className="text-center">
                <p className="text-gray-700 font-semibold">No pending products!</p>
                <p className="text-gray-400 text-sm mt-1">
                  {search ? 'No products match your search' : 'All products reviewed. Add new products to see them here.'}
                </p>
                {!search && (
                  <Link to="/product-listing"
                    className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line"></i>List New Product
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Pending header */}
              <div className="flex items-center gap-3 px-1">
                <div className="w-8 h-8 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Awaiting Approval</p>
                  <p className="text-gray-400 text-xs">{pendingProducts.length} product{pendingProducts.length > 1 ? 's' : ''} pending review</p>
                </div>
                <div className="ml-auto">
                  <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold border border-amber-200">
                    Review Needed
                  </span>
                </div>
              </div>
              {pendingProducts.map(product => (
                <ProductCard key={product.id} product={product}
                  onSelect={setSelectedProduct} onApprove={handleApprove} onDelete={handleDelete} />
              ))}
            </div>
          )}

          {/* Rejected products */}
          {rejectedProducts.length > 0 && (
            <div className="space-y-3 mt-4">
              <div className="flex items-center gap-3 px-1">
                <div className="w-8 h-8 rounded-xl bg-red-400 flex items-center justify-center shrink-0">
                  <i className="ri-close-circle-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Rejected Products</p>
                  <p className="text-gray-400 text-xs">{rejectedProducts.length} product{rejectedProducts.length > 1 ? 's' : ''} rejected</p>
                </div>
              </div>
              {rejectedProducts.map(product => (
                <ProductCard key={product.id} product={product}
                  onSelect={setSelectedProduct} onApprove={handleApprove} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── CATEGORY WISE SECTION ── */}
      {viewMode === 'approved' && (
        <>
          {/* Category tabs */}
          {displayCategories.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button onClick={() => setActiveCategory('all')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                  activeCategory === 'all' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}>
                <i className="ri-apps-line"></i>All ({approvedProducts.length})
              </button>
              {displayCategories.map(cat => {
                const cnt = approvedProducts.filter(p => p.category === cat).length;
                const icon = CATEGORY_ICONS[cat] ?? 'ri-shopping-bag-line';
                const color = CATEGORY_COLORS[cat] ?? 'bg-gray-500';
                return (
                  <button key={cat} onClick={() => setActiveCategory(cat)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                      activeCategory === cat ? `${color} text-white` : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}>
                    <i className={icon}></i>{cat} ({cnt})
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {approvedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-2xl text-gray-400"></i>
              </div>
              <div className="text-center">
                <p className="text-gray-700 font-semibold">No approved products</p>
                <p className="text-gray-400 text-sm mt-1">Go to &quot;Pending Review&quot; to approve products first.</p>
              </div>
            </div>
          ) : activeCategory === 'all' ? (
            /* Show all categories grouped */
            <div className="space-y-6">
              {displayCategories.map(cat => {
                const catProducts = approvedProducts.filter(p => p.category === cat);
                if (catProducts.length === 0) return null;
                const icon = CATEGORY_ICONS[cat] ?? 'ri-shopping-bag-line';
                const color = CATEGORY_COLORS[cat] ?? 'bg-gray-500';
                return (
                  <div key={cat} className="space-y-3">
                    {/* Category header */}
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center shrink-0`}>
                        <i className={`${icon} text-white text-sm`}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900 font-bold text-sm">{cat}</p>
                        <p className="text-gray-400 text-xs">{catProducts.length} product{catProducts.length > 1 ? 's' : ''} live</p>
                      </div>
                      <span className="px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-bold border border-green-200">
                        Live in Store
                      </span>
                    </div>
                    {catProducts.map(product => (
                      <ProductCard key={product.id} product={product}
                        onSelect={setSelectedProduct} onApprove={handleApprove} onDelete={handleDelete} />
                    ))}
                  </div>
                );
              })}

              {/* Other categories with 0 products */}
              {ALL_CATEGORIES.filter(cat => !displayCategories.includes(cat)).map(cat => {
                const icon = CATEGORY_ICONS[cat] ?? 'ri-shopping-bag-line';
                const color = CATEGORY_COLORS[cat] ?? 'bg-gray-500';
                return (
                  <div key={cat} className="bg-white rounded-2xl border border-dashed border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl ${color} opacity-40 flex items-center justify-center shrink-0`}>
                        <i className={`${icon} text-white text-sm`}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-500 font-semibold text-sm">{cat}</p>
                        <p className="text-gray-400 text-xs">No products yet</p>
                      </div>
                      <Link to="/product-listing"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-add-line"></i>Add Products
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Single category view */
            <div className="space-y-3">
              {filteredApproved.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-gray-100">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                    <i className="ri-search-line text-xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500 text-sm">No products found for &quot;{activeCategory}&quot;</p>
                </div>
              ) : (
                filteredApproved.map(product => (
                  <ProductCard key={product.id} product={product}
                    onSelect={setSelectedProduct} onApprove={handleApprove} onDelete={handleDelete} />
                ))
              )}
            </div>
          )}
        </>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDelete}
          onUpdateStock={handleUpdateStock}
          onToggleTrending={handleToggleTrending}
          onUpdateImages={handleUpdateImages}
          onUpdateVideo={handleUpdateVideo}
        />
      )}
    </div>
  );
}
